import React, { useState, useEffect } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  FormGroup,
} from "reactstrap";
import { getCustomerMemberships, saveCustomerMembership } from "../../components/Api/GmsCustomerMembership";
import { getCustomers, searchCustomers } from "../../components/Api/GmsCustomer";
import { getBranches } from "../../components/Api/GmsBranch";
import { getSpaPackages } from "../../components/Api/GmsSpaPackage";
import { getSpaPackageSeries } from "../../components/Api/GmsSpaPackageSeries";
import { getSpaServices } from "../../components/Api/GmsSpaService";
import { getProducts } from "../../components/Api/GmsProduct";
import { getEmployees } from "../../components/Api/GmsEmployee";
import { Eye, SquarePen, User, Search } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import {
  hasPagePermissionWithAdmin,
  hasViewOnlyPermission,
  hasEditOrHigherPermission,
  hasDeletePermissionWithAdmin,
  isUserAdmin
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import QuickAddCustomer from "../../components/QuickAddCustomer";
import CustomerProfileModal from "../../components/CustomerProfileModal";
import CustomerAdvancedSearchModal from "../../components/CustomerAdvancedSearchModal";

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const getDefaultForm = () => {
  // Get user's branch ID from localStorage if available
  const userBranchId = localStorage.getItem("userBranchId");

  return {
    MembershipId: 0,
    CustomerId: null,
    BranchId: userBranchId ? Number(userBranchId) : null,
    Entity_Id: null,
    Entity_Type: null,
    PurchaseDate: new Date().toISOString().split('T')[0],
    SalesmanId: null,  // Kept for backward compatibility
    SalesmanIds: [],  // Array of salesman IDs for multi-salesman support
    CreatedBy: 1,
    ModifiedBy: 1,
  };
};

const emptyFilter = {
  MembershipId: null,
  CustomerId: null,
  BranchId: null,
  Entity_Type: null,
  SalesmanId: null,
  From_Date: null,
  To_Date: null,
};

export default function MembershipPage() {
  const [memberships, setMemberships] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerSearchOptions, setCustomerSearchOptions] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);
  const [selectedCustomerFilterOption, setSelectedCustomerFilterOption] = useState(null);
  const [showAdvancedSearchModal, setShowAdvancedSearchModal] = useState(false);
  const [branches, setBranches] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);
  const [spaPackages, setSpaPackages] = useState([]);
  const [spaPackageSeries, setSpaPackageSeries] = useState([]);
  const [spaServices, setSpaServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [form, setForm] = useState(getDefaultForm());
  const [filter, setFilter] = useState(emptyFilter);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [editLoadingRow, setEditLoadingRow] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState("1");
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Check if user has a specific branch assigned
  const userBranchId = localStorage.getItem("userBranchId");
  const isBranchReadOnly = !!userBranchId;

  // Quick Add Customer state
  const [showQuickAddCustomer, setShowQuickAddCustomer] = useState(false);
  const [showCustomerProfile, setShowCustomerProfile] = useState(false);
  const [formKey, setFormKey] = useState(0); // Key to force form re-render

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsMembership');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsMembership');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsMembership');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsMembership');
  const isAdmin = isUserAdmin();

  // Session management
  const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return {
      userId: authUser.userId || 1,
      userName: authUser.userName || "System",
      branchId: authUser.branchId || user.user_Branch_Id || null,
    };
  };

  useEffect(() => {
    if (hasPermission) {
      loadCustomers();
      loadBranches();
      loadSpaPackages();
      loadSpaPackageSeries();
      loadSpaServices();
      loadProducts();
      loadEmployees();
      loadMemberships();

      // Set default tab based on permissions
      if (isViewOnly) {
        setActiveTab("2"); // Show list tab for view-only users
      }
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, isViewOnly]);

  useEffect(() => {
    if (hasAppliedFilter) {
      loadMemberships();
      setHasAppliedFilter(false);
    }
  }, [hasAppliedFilter]);

  const loadCustomers = async () => {
    try {
      const data = await getCustomers({ customer_Active: true });
      setCustomers(data || []);
    } catch (error) {
      console.error("Error loading customers:", error);
      Swal.fire("Error", "Failed to load customers", "error");
    }
  };

  // Manual search function for customer search
  const searchCustomersManually = async (inputValue) => {
    console.log('🔍 searchCustomersManually called with inputValue:', inputValue);

    if (!inputValue || inputValue.length < 3) {
      console.log('❌ Input too short, clearing options');
      setCustomerSearchOptions([]);
      return;
    }

    try {
      setCustomerSearchLoading(true);
      console.log('🚀 Making API call to searchCustomers with:', { inputValue, page: 1, pageSize: 20 });
      const response = await searchCustomers(inputValue, 1, 20);
      console.log('✅ API response received:', response);

      const formattedOptions = response.items.map(customer => ({
        value: customer.customer_Id,
        label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
        customer: customer
      }));

      console.log('📋 Formatted options:', formattedOptions);
      setCustomerSearchOptions(formattedOptions);
    } catch (error) {
      console.error('❌ Error searching customers:', error);
      setCustomerSearchOptions([]);
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  const loadBranches = async () => {
    try {
      const data = await getBranches({ branch_Active: true });
      const options = data.map(branch => ({
        value: branch.branch_Id,
        label: branch.branch_NameEn
      }));
      setBranchOptions(options);
      setBranches(data || []);
    } catch (error) {
      console.error("Error loading branches:", error);
      Swal.fire("Error", "Failed to load branches", "error");
    }
  };

  const loadSpaPackages = async () => {
    try {
      const data = await getSpaPackages({ Status: true });
      setSpaPackages(data || []);
    } catch (error) {
      console.error("Error loading spa packages:", error);
      Swal.fire("Error", "Failed to load spa packages", "error");
    }
  };

  const loadSpaPackageSeries = async () => {
    try {
      const data = await getSpaPackageSeries({ Status: true });
      setSpaPackageSeries(data || []);
    } catch (error) {
      console.error("Error loading spa package series:", error);
      Swal.fire("Error", "Failed to load spa package series", "error");
    }
  };

  const loadSpaServices = async () => {
    try {
      const data = await getSpaServices({ Status: true });
      setSpaServices(data || []);
    } catch (error) {
      console.error("Error loading spa services:", error);
      Swal.fire("Error", "Failed to load spa services", "error");
    }
  };

  const loadProducts = async () => {
    try {
      const data = await getProducts({ Product_Status: true });
      setProducts(data || []);
    } catch (error) {
      console.error("Error loading products:", error);
      // Don't show error for products, just log it
    }
  };

  const loadEmployees = async () => {
    try {
      const data = await getEmployees({});
      setEmployees(data || []);
      const options = (data || []).map(emp => ({
        value: emp.employee_Id,
        label: `${emp.employee_Code || ''} - ${emp.employee_NameEn || 'N/A'}`.trim()
      }));
      setEmployeeOptions(options);
    } catch (error) {
      console.error("Error loading employees:", error);
      Swal.fire("Error", "Failed to load employees", "error");
    }
  };

  const loadMemberships = async () => {
    try {
      setLoading(true);
      const data = await getCustomerMemberships(filter);

      // Transform API response to match frontend expectations
      const transformedData = (data || []).map(membership => ({
        MembershipId: membership.membershipId,
        CustomerId: membership.customerId,
        BranchId: membership.branchId,
        Entity_Id: membership.entity_Id,
        Entity_Type: membership.entity_Type,
        PurchaseDate: membership.purchaseDate,
        SalesmanId: membership.salesmanId || null,
        SalesmanIds: membership.salesmanIds || null,  // Add SalesmanIds from API response
        CreatedOn: membership.createdOn,
        CreatedBy: membership.createdBy,
        ModifiedOn: membership.modifiedOn,
        ModifiedBy: membership.modifiedBy,
        details: membership.details || [],
        installments: (membership.installments || []).map(installment => ({
          installmentId: installment.installmentId || installment.m_InstallmentId,
          installmentName: installment.installmentName,
          amount: installment.installmentAmount || installment.amount,
          paidAmount: installment.paidAmount || 0,
          billedAmount: installment.billedAmount || 0  // Add billedAmount to mapped object
        }))
      }));

      setMemberships(transformedData);
      setTotalCount(transformedData.length);
    } catch (error) {
      console.error("Error loading memberships:", error);
      Swal.fire("Error", "Failed to load memberships", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      // Prevent multiple submissions
      if (saveLoading) {
        return;
      }

      // Validation
      if (!form.CustomerId) {
        Swal.fire("Error", "Please select a customer", "error");
        return;
      }
      if (!form.BranchId) {
        Swal.fire("Error", "Please select a branch", "error");
        return;
      }
      if (!form.Entity_Type) {
        Swal.fire("Error", "Please select entity type", "error");
        return;
      }
      if (!form.Entity_Id) {
        Swal.fire("Error", "Please select a package or package series", "error");
        return;
      }
      if (!form.PurchaseDate) {
        Swal.fire("Error", "Please select purchase date", "error");
        return;
      }

      // Add validation for Salesman
      const hasSalesman = (form.SalesmanIds && form.SalesmanIds.length > 0) || (form.SalesmanId && form.SalesmanId > 0);
      if (!hasSalesman) {
        Swal.fire("Error", "Please select at least one salesman", "error");
        return;
      }

      setSaveLoading(true);
      const session = getUserSession();

      // Get all salesman IDs for multi-salesman support
      const salesmanIds = form.SalesmanIds && Array.isArray(form.SalesmanIds) && form.SalesmanIds.length > 0
        ? form.SalesmanIds
        : (form.SalesmanId && form.SalesmanId > 0 ? [form.SalesmanId] : []);

      const saveData = {
        ...form,
        SalesmanId: salesmanIds.length > 0 ? salesmanIds[0] : null,  // Keep for backward compatibility (will be ignored by stored procedure)
        SalesmanIds: salesmanIds,  // Array of salesman IDs for multi-salesman support
        CreatedBy: isEditing ? form.CreatedBy : session.userId,
        ModifiedBy: session.userId,
        CreatedOn: isEditing ? form.CreatedOn : new Date().toISOString(),
        ModifiedOn: new Date().toISOString(),
      };

      const result = await saveCustomerMembership(saveData);
      Swal.fire("Success", "Membership saved successfully", "success");
      handleClear();
      loadMemberships();
      setActiveTab("2");
    } catch (error) {
      console.error("Error saving membership:", error);
      const errorMessage = error.response?.data?.message || "Failed to save membership";
      Swal.fire("Error", errorMessage, "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleEdit = async (membership) => {
    try {
      setEditLoadingRow(membership.MembershipId);

      // Get salesmen from junction table if available, otherwise use single SalesmanId
      const salesmanIds = membership.SalesmanIds && Array.isArray(membership.SalesmanIds) && membership.SalesmanIds.length > 0
        ? membership.SalesmanIds
        : (membership.SalesmanId && membership.SalesmanId > 0 ? [membership.SalesmanId] : []);

      setForm({
        MembershipId: membership.MembershipId,
        CustomerId: membership.CustomerId,
        BranchId: membership.BranchId,
        Entity_Id: membership.Entity_Id,
        Entity_Type: membership.Entity_Type,
        PurchaseDate: membership.PurchaseDate ? membership.PurchaseDate.split('T')[0] : '',
        SalesmanId: membership.SalesmanId || null,  // Kept for backward compatibility
        SalesmanIds: salesmanIds,  // Array of salesman IDs for multi-salesman support
        CreatedBy: membership.CreatedBy,
        CreatedOn: membership.CreatedOn,
        ModifiedBy: membership.ModifiedBy,
        ModifiedOn: membership.ModifiedOn,
      });

      // Set the selected customer option for display
      const customer = customers.find(c => c.customer_Id === membership.CustomerId);
      if (customer) {
        setSelectedCustomerOption({
          value: customer.customer_Id,
          label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`
        });
      }

      setIsEditing(true);
      setActiveTab("1");
    } catch (error) {
      console.error("Error loading membership for edit:", error);
      Swal.fire("Error", "Failed to load membership for editing", "error");
    } finally {
      setEditLoadingRow(null);
    }
  };

  const handleView = async (membership) => {
    try {
      const customer = customers.find(c => c.customer_Id === membership.CustomerId);
      const branch = branches.find(b => b.branch_Id === membership.BranchId);

      // Get all salesmen from junction table if available, otherwise use single SalesmanId
      // Handle both camelCase (from API) and PascalCase (from transformed data)
      const salesmanIds = (membership.salesmanIds && Array.isArray(membership.salesmanIds) && membership.salesmanIds.length > 0)
        ? membership.salesmanIds
        : (membership.SalesmanIds && Array.isArray(membership.SalesmanIds) && membership.SalesmanIds.length > 0)
          ? membership.SalesmanIds
          : ((membership.salesmanId && membership.salesmanId > 0) ? [membership.salesmanId] : ((membership.SalesmanId && membership.SalesmanId > 0) ? [membership.SalesmanId] : []));

      const salesmanNames = salesmanIds
        .map(id => {
          const employee = employees.find(e => e.employee_Id === id);
          return employee ? `${employee.employee_Code || ''} - ${employee.employee_NameEn || 'N/A'}`.trim() : null;
        })
        .filter(name => name !== null);

      const salesmanName = salesmanNames.length > 0 ? salesmanNames.join(", ") : 'N/A';
      const entityName = membership.Entity_Type === 'pac'
        ? spaPackages.find(p => p.packageId === membership.Entity_Id)?.packageName_En
        : spaPackageSeries.find(s => s.seriesId === membership.Entity_Id)?.seriesName_En;

      // Generate detailed items HTML (for sub-popup)
      const generateDetailedItemsHtml = () => {
        if (!membership.details || membership.details.length === 0) {
          return '<div style="text-align: center; color: #6c757d; padding: 20px;">No package, service, or product details available</div>';
        }

        return (() => {
          // Group details by package, then by service/product, and count quantities
          const groupedByPackage = membership.details.reduce((acc, detail) => {
            const packageKey = detail.packageId ? `pkg_${detail.packageId}` : 'direct';
            const packageName = detail.packageId ? getPackageName(detail.packageId) : 'Direct Items';

            if (!acc[packageKey]) {
              acc[packageKey] = {
                packageId: detail.packageId,
                packageName: packageName,
                packageQuantity: 0,
                services: {},
                products: {}
              };
            }

            // Count package instances (for packages, count unique package occurrences)
            if (detail.packageId) {
              acc[packageKey].packageQuantity++;
            }

            if (detail.serviceId) {
              const serviceKey = `svc_${detail.serviceId}`;
              if (!acc[packageKey].services[serviceKey]) {
                acc[packageKey].services[serviceKey] = {
                  serviceId: detail.serviceId,
                  serviceName: getServiceName(detail.serviceId),
                  quantity: 0,
                  completed: 0,
                  inProgress: 0,
                  pending: 0
                };
              }
              acc[packageKey].services[serviceKey].quantity++;
              const serviceStatus = (detail.completionStatus || 'Pending').toString().trim();
              const statusLower = serviceStatus.toLowerCase();
              if (statusLower === 'completed' || statusLower === 'complete') {
                acc[packageKey].services[serviceKey].completed++;
              } else if (statusLower === 'in progress' || statusLower === 'inprogress' || statusLower === 'check in' || statusLower === 'checkin') {
                acc[packageKey].services[serviceKey].inProgress++;
              } else {
                acc[packageKey].services[serviceKey].pending++;
              }
            }

            if (detail.productId) {
              const productKey = `prod_${detail.productId}`;
              if (!acc[packageKey].products[productKey]) {
                acc[packageKey].products[productKey] = {
                  productId: detail.productId,
                  productName: getProductName(detail.productId),
                  quantity: 0,
                  completed: 0,
                  inProgress: 0,
                  pending: 0
                };
              }
              acc[packageKey].products[productKey].quantity++;
              const productStatus = (detail.completionStatus || 'Pending').toString().trim();
              const statusLower = productStatus.toLowerCase();
              if (statusLower === 'completed' || statusLower === 'complete') {
                acc[packageKey].products[productKey].completed++;
              } else if (statusLower === 'in progress' || statusLower === 'inprogress' || statusLower === 'check in' || statusLower === 'checkin') {
                acc[packageKey].products[productKey].inProgress++;
              } else {
                acc[packageKey].products[productKey].pending++;
              }
            }

            return acc;
          }, {});

          // For package quantity display, we'll calculate it based on the pattern
          // If Package X appears 2 times in series, and has Service A (qty 2 per package), Service A will have 4 total
          // We can estimate by finding the ratio
          Object.keys(groupedByPackage).forEach(key => {
            if (key !== 'direct' && groupedByPackage[key].packageId) {
              const servicesList = Object.values(groupedByPackage[key].services);
              const productsList = Object.values(groupedByPackage[key].products);

              if (servicesList.length > 0 || productsList.length > 0) {
                // Simple heuristic: use the minimum quantity as package quantity estimate
                // This works if all items have similar quantities per package instance
                const allQuantities = [
                  ...servicesList.map(s => s.quantity),
                  ...productsList.map(p => p.quantity)
                ];

                if (allQuantities.length > 0) {
                  // Find GCD to estimate package quantity
                  const gcd = (a, b) => b === 0 ? a : gcd(b, a % b);
                  const findGCD = (arr) => {
                    if (arr.length === 0) return 1;
                    if (arr.length === 1) return arr[0];
                    let result = arr[0];
                    for (let i = 1; i < arr.length; i++) {
                      result = gcd(result, arr[i]);
                    }
                    return result;
                  };

                  const estimatedQty = Math.max(1, findGCD(allQuantities));
                  groupedByPackage[key].packageQuantity = estimatedQty;
                } else {
                  groupedByPackage[key].packageQuantity = 1;
                }
              } else {
                groupedByPackage[key].packageQuantity = 1;
              }
            }
          });

          // Generate tree-structured HTML
          const getStatusText = (completed, inProgress, pending, total) => {
            if (completed === total) {
              return '<span class="badge bg-success" style="font-size: 11px; padding: 4px 8px;">All Completed</span>';
            } else if (completed > 0 || inProgress > 0) {
              return `<span class="badge bg-warning" style="font-size: 11px; padding: 4px 8px;">${completed} Completed, ${inProgress} In Progress, ${pending} Pending</span>`;
            } else {
              return '<span class="badge bg-secondary" style="font-size: 11px; padding: 4px 8px;">All Pending</span>';
            }
          };

          let html = '';

          // Process packages (non-direct items)
          const packages = Object.values(groupedByPackage).filter(p => p.packageName !== 'Direct Items');
          packages.forEach((packageData) => {
            const servicesList = Object.values(packageData.services);
            const productsList = Object.values(packageData.products);
            const totalServicesQty = servicesList.reduce((sum, s) => sum + s.quantity, 0);
            const totalProductsQty = productsList.reduce((sum, p) => sum + p.quantity, 0);

            html += `
              <div style="margin-bottom: 12px; padding-left: 0; width: 100%;">
                <div style="display: flex; align-items: center; margin-bottom: 6px;">
                  <span style="color: #495057; font-weight: 600; font-size: 14px;">
                    📦 Packages (${packageData.packageQuantity}) - ${packageData.packageName} (${packageData.packageQuantity})
                  </span>
                </div>
                ${servicesList.length > 0 ? `
                  <div style="padding-left: 20px; margin-bottom: 6px;">
                    <div style="color: #6c757d; font-size: 14px; margin-bottom: 4px;">
                      Services (${totalServicesQty})
                    </div>
                    ${servicesList.map(service => `
                      <div style="padding-left: 20px; margin-bottom: 3px; display: flex; align-items: center; justify-content: space-between; width: 100%;">
                        <div style="display: flex; align-items: center; gap: 8px; flex: 1;">
                          <span style="color: #495057; font-size: 14px;">${service.serviceName}</span>
                          <span style="color: #556ee6; font-weight: 500; font-size: 14px;">(${service.quantity})</span>
                        </div>
                        <div style="flex-shrink: 0;">
                          ${getStatusText(service.completed, service.inProgress, service.pending, service.quantity)}
                        </div>
                      </div>
                    `).join('')}
                  </div>
                ` : ''}
                ${productsList.length > 0 ? `
                  <div style="padding-left: 20px;">
                    <div style="color: #6c757d; font-size: 14px; margin-bottom: 4px;">
                      Products (${totalProductsQty})
                    </div>
                    ${productsList.map(product => `
                      <div style="padding-left: 20px; margin-bottom: 3px; display: flex; align-items: center; justify-content: space-between; width: 100%;">
                        <div style="display: flex; align-items: center; gap: 8px; flex: 1;">
                          <span style="color: #495057; font-size: 14px;">${product.productName}</span>
                          <span style="color: #556ee6; font-weight: 500; font-size: 14px;">(${product.quantity})</span>
                        </div>
                        <div style="flex-shrink: 0;">
                          ${getStatusText(product.completed, product.inProgress, product.pending, product.quantity)}
                        </div>
                      </div>
                    `).join('')}
                  </div>
                ` : ''}
              </div>
              `;
          });

          // Process direct items
          const directItems = groupedByPackage['direct'];
          if (directItems) {
            const servicesList = Object.values(directItems.services);
            const productsList = Object.values(directItems.products);
            const totalServicesQty = servicesList.reduce((sum, s) => sum + s.quantity, 0);
            const totalProductsQty = productsList.reduce((sum, p) => sum + p.quantity, 0);

            if (servicesList.length > 0 || productsList.length > 0) {
              html += `
                <div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid #e9ecef;">
                  <div style="color: #495057; font-weight: 600; font-size: 14px; margin-bottom: 8px;">
                    📋 Direct
                  </div>
                  ${servicesList.length > 0 ? `
                    <div style="padding-left: 20px; margin-bottom: 6px;">
                      <div style="color: #6c757d; font-size: 14px; margin-bottom: 4px;">
                        Services (${totalServicesQty})
                      </div>
                      ${servicesList.map(service => `
                        <div style="padding-left: 20px; margin-bottom: 3px; display: flex; align-items: center; justify-content: space-between; width: 100%;">
                          <div style="display: flex; align-items: center; gap: 8px; flex: 1;">
                            <span style="color: #495057; font-size: 14px;">${service.serviceName}</span>
                            <span style="color: #556ee6; font-weight: 500; font-size: 14px;">(${service.quantity})</span>
                          </div>
                          <div style="flex-shrink: 0;">
                            ${getStatusText(service.completed, service.inProgress, service.pending, service.quantity)}
                          </div>
                        </div>
                      `).join('')}
                    </div>
                  ` : ''}
                  ${productsList.length > 0 ? `
                    <div style="padding-left: 20px;">
                    <div style="color: #6c757d; font-size: 14px; margin-bottom: 4px;">
                      Products (${totalProductsQty})
                    </div>
                    ${productsList.map(product => `
                      <div style="padding-left: 20px; margin-bottom: 3px; display: flex; align-items: center; justify-content: space-between; width: 100%;">
                        <div style="display: flex; align-items: center; gap: 8px; flex: 1;">
                          <span style="color: #495057; font-size: 14px;">${product.productName}</span>
                          <span style="color: #556ee6; font-weight: 500; font-size: 14px;">(${product.quantity})</span>
                        </div>
                        <div style="flex-shrink: 0;">
                          ${getStatusText(product.completed, product.inProgress, product.pending, product.quantity)}
                        </div>
                      </div>
                    `).join('')}
                    </div>
                  ` : ''}
                </div>
                `;
            }
          }

          return html || '<div style="text-align: center; color: #6c757d; padding: 20px;">No details available</div>';
        })();
      };

      // Function to show detailed items popup
      const showDetailedItemsPopup = () => {
        const detailedItemsHtml = generateDetailedItemsHtml();

        // Close current popup first
        Swal.close();

        // Small delay to ensure smooth transition
        setTimeout(() => {
          let isClosingFromBackButton = false;

          const detailedSwalInstance = Swal.fire({
            title: `<div style="text-align: center; color: #556ee6; font-size: 18px; font-weight: bold; margin-bottom: 10px;">
                      <i class="bx bx-list-ul" style="margin-right: 10px;"></i>
                      Package, Service & Product Details
                    </div>`,
            html: `
              <div style="max-width: 800px; margin: 0 auto;">
                <div style="margin-bottom: 15px; padding: 12px; background: #f8f9fa; border-radius: 6px; border: 1px solid #dee2e6;">
                  <strong style="color: #495057; font-size: 14px;">Total Items: ${membership.details.length}</strong>
                </div>
              <div style="max-height: 500px; overflow-y: auto; padding: 10px; background: #fff; border-radius: 6px; border: 1px solid #e9ecef; width: 100%;">
                <div style="width: 100%;">
                  ${detailedItemsHtml}
                </div>
              </div>
              </div>
               <div style="text-align: center; margin-top: 16px;">
                 <button
                   id="membershipDetailsBackBtn_${membership.MembershipId}"
                   type="button"
                   style="
                     background: #556ee6;
                     color: white;
                     border: none;
                     padding: 10px 22px;
                     border-radius: 6px;
                     cursor: pointer;
                     font-weight: 500;
                     font-size: 14px;
                     pointer-events: auto !important;
                     z-index: 20001;
                     position: relative;
                   "
                   onmouseover="this.style.background='#4858d4'"
                   onmouseout="this.style.background='#556ee6'"
                   onclick="if(window.__handleBackClick_${membership.MembershipId}) { window.__handleBackClick_${membership.MembershipId}(); } else { if(window.Swal) window.Swal.close(); }"
                 >
                   Back
                 </button>
               </div>
            `,
            width: '900px',
            showConfirmButton: false,
            allowOutsideClick: false,
            allowEscapeKey: true,
            showClass: {
              popup: 'animate__animated animate__fadeInDown'
            },
            hideClass: {
              popup: 'animate__animated animate__fadeOutUp'
            },
            customClass: {
              container: 'swal2-top-layer'
            },
            didOpen: () => {
              // Ensure SweetAlert is top-most so clicks don't "pass through"
              const container = Swal.getContainer?.();
              if (container) {
                container.style.zIndex = '20000';
              }

              // Create a robust close function for detailed modal that forces immediate close
              const forceCloseDetailedModal = () => {
                isClosingFromBackButton = true;

                // Immediately try to close using Swal API
                try {
                  Swal.close();
                } catch (e) {
                  // Silently handle errors
                }

                // Safely remove SweetAlert elements
                const removeSwalElements = () => {
                  try {
                    // Get Swal elements using API
                    const popup = Swal.getPopup?.();
                    const container = Swal.getContainer?.();

                    // Remove popup if it exists
                    if (popup && popup.parentNode) {
                      popup.style.display = 'none';
                      popup.remove();
                    }

                    // Remove container if it exists
                    if (container && container.parentNode) {
                      container.style.display = 'none';
                      container.remove();
                    }

                    // Remove backdrop/overlay - be more specific
                    const backdrop = document.querySelector('.swal2-backdrop-show');
                    if (backdrop && backdrop.parentNode) {
                      backdrop.style.display = 'none';
                      backdrop.remove();
                    }

                    // Also try the standard backdrop class
                    const standardBackdrop = document.querySelector('.swal2-backdrop');
                    if (standardBackdrop && standardBackdrop.parentNode && standardBackdrop !== backdrop) {
                      standardBackdrop.style.display = 'none';
                      standardBackdrop.remove();
                    }

                    // Safely remove body classes - check if body exists
                    if (document.body) {
                      document.body.classList.remove('swal2-shown', 'swal2-height-auto', 'swal2-no-backdrop');
                      if (document.body.style) {
                        document.body.style.overflow = '';
                        document.body.style.paddingRight = '';
                      }
                    }
                  } catch (error) {
                    // Silently handle errors
                  }
                };

                // Execute immediately
                removeSwalElements();

                // Also execute after short delays to catch any that appear
                setTimeout(() => {
                  removeSwalElements();
                  // Reopen main modal
                  setTimeout(() => {
                    handleView(membership);
                  }, 100);
                }, 50);

                setTimeout(() => {
                  removeSwalElements();
                }, 150);
              };

              // Create global function for onclick fallback
              window[`__handleBackClick_${membership.MembershipId}`] = forceCloseDetailedModal;

              // Use setTimeout to ensure DOM is fully ready
              setTimeout(() => {
                // Attach click handler to the back button
                const backBtn = document.getElementById(`membershipDetailsBackBtn_${membership.MembershipId}`);

                if (backBtn) {
                  backBtn.disabled = false;
                  backBtn.style.pointerEvents = 'auto';
                  backBtn.style.opacity = '1';
                  backBtn.style.cursor = 'pointer';
                  backBtn.style.zIndex = '20001';
                  backBtn.style.position = 'relative';

                  // Create handler using the force close function
                  const backHandler = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    forceCloseDetailedModal();
                  };

                  // Remove any existing listener first
                  if (backBtn._backHandler) {
                    backBtn.removeEventListener('click', backBtn._backHandler);
                  }

                  // Store handler reference for cleanup
                  backBtn._backHandler = backHandler;

                  // Add event listener with capture to ensure it fires
                  backBtn.addEventListener('click', backHandler, { capture: true });

                  // Also try mousedown as fallback
                  backBtn.addEventListener('mousedown', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    forceCloseDetailedModal();
                  });
                }
              }, 100);
            },
            didClose: () => {
              // Clean up global function
              if (window[`__handleBackClick_${membership.MembershipId}`]) {
                delete window[`__handleBackClick_${membership.MembershipId}`];
              }

              // Clean up event listener
              const backBtn = document.getElementById(`membershipDetailsBackBtn_${membership.MembershipId}`);
              if (backBtn && backBtn._backHandler) {
                backBtn.removeEventListener('click', backBtn._backHandler, { capture: true });
                delete backBtn._backHandler;
              }

              // Only reopen the main membership details popup if closed via Back button
              // Don't reopen if closed via ESC or other means
              if (isClosingFromBackButton) {
                setTimeout(() => {
                  handleView(membership);
                }, 100);
              }
              isClosingFromBackButton = false;
            }
          });
        }, 100);
      };

      // Generate simplified summary for main popup
      const packageServiceDetailsHtml = membership.details && membership.details.length > 0
        ? (() => {
          // Just show summary counts
          const packageCount = new Set(membership.details.filter(d => d.packageId).map(d => d.packageId)).size;
          const serviceCount = new Set(membership.details.filter(d => d.serviceId).map(d => d.serviceId)).size;
          const productCount = new Set(membership.details.filter(d => d.productId).map(d => d.productId)).size;

          return `
              <div style="text-align: center; padding: 15px;">
                <div style="margin-bottom: 15px;">
                  <strong style="color: #495057; font-size: 14px;">Total Items: ${membership.details.length}</strong>
                </div>
                <div style="display: flex; justify-content: center; gap: 20px; margin-bottom: 15px; flex-wrap: wrap;">
                  ${packageCount > 0 ? `<div><span style="color: #6c757d;">📦 Packages:</span> <strong style="color: #495057;">${packageCount}</strong></div>` : ''}
                  ${serviceCount > 0 ? `<div><span style="color: #6c757d;">💆 Services:</span> <strong style="color: #495057;">${serviceCount}</strong></div>` : ''}
                  ${productCount > 0 ? `<div><span style="color: #6c757d;">📦 Products:</span> <strong style="color: #495057;">${productCount}</strong></div>` : ''}
                </div>
                <button 
                  id="viewDetailsBtn_${membership.MembershipId}"
                  style="
                    background: #556ee6; 
                    color: white; 
                    border: none; 
                    padding: 10px 20px; 
                    border-radius: 6px; 
                    cursor: pointer; 
                    font-weight: 500;
                    font-size: 14px;
                    transition: background 0.3s;
                  "
                  onmouseover="this.style.background='#4858d4'"
                  onmouseout="this.style.background='#556ee6'"
                >
                  <i class="bx bx-list-ul" style="margin-right: 5px;"></i>
                  View Detailed Items
                </button>
              </div>
            `;
        })()
        : '<div style="text-align: center; color: #6c757d; padding: 20px;">No package, service, or product details available</div>';

      // Fetch package series data to get installment names
      let installmentNames = {};
      if (membership.installments && membership.installments.length > 0 && membership.Entity_Type === 'sep') {
        try {
          const seriesData = await getSpaPackageSeries({ seriesId: membership.Entity_Id });
          if (seriesData && seriesData.length > 0 && seriesData[0].installments) {
            seriesData[0].installments.forEach(installment => {
              installmentNames[installment.installmentId] = installment.installmentName;
            });
          }
        } catch (error) {
          console.error('Error fetching package series data:', error);
        }
      }

      // Calculate total balance (total amount - total billed amount)
      const totalAmount = membership.installments ? membership.installments.reduce((sum, installment) => sum + (installment.amount || 0), 0) : 0;
      const totalBilledAmount = membership.installments ? membership.installments.reduce((sum, installment) => sum + (installment.billedAmount || 0), 0) : 0;
      const totalBalance = totalAmount - totalBilledAmount;

      // Generate installments HTML with proper names
      const installmentsHtml = membership.installments && membership.installments.length > 0
        ? membership.installments.map(installment => {
          // For packages (SeriesId is NULL/0), show "Package Payment" instead of "Installment 1"
          const isPackage = membership.Entity_Type === 'PAC' && (!installment.seriesId || installment.seriesId === 0);
          const installmentName = isPackage
            ? 'Package Payment'
            : (installmentNames[installment.installmentId] || `Installment ${installment.installmentId}`);
          return `
            <div style="background: #fff; padding: 10px; border-radius: 6px; border: 1px solid #e9ecef; margin-bottom: 8px;">
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <div>
                  <span style="color: #495057; font-size: 14px; font-weight: ${isPackage ? '600' : '400'};">${installmentName}:</span>
                  <span style="color: #6c757d; margin-left: 5px; font-size: 14px;">${(installment.amount || 0).toFixed(2)}</span>
                </div>
                <div>
                  <span style="color: #6c757d; font-size: 14px;">Billed: ${(installment.billedAmount || 0).toFixed(2)}</span>
                  <span class="badge ${(installment.billedAmount || 0) >= (installment.amount || 0) ? 'bg-success' : 'bg-warning'} ms-2" style="font-size: 11px;">
                    ${(installment.billedAmount || 0) >= (installment.amount || 0) ? 'Billed' : 'Pending'}
                  </span>
                </div>
              </div>
            </div>
          `;
        }).join('')
        : '<div style="text-align: center; color: #6c757d; padding: 20px;">No installment details available</div>';

      const swalInstance = Swal.fire({
        title: `<div style="text-align: center; color: #556ee6; font-size: 18px; font-weight: bold; margin-bottom: 10px;">
                  <i class="bx bx-id-card" style="margin-right: 10px;"></i>
                  Membership Details
                </div>`,
        html: `
          <div style="max-width: 900px; margin: 0 auto;">
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 15px;">
              <div style="background: #f8f9fa; padding: 12px; border-radius: 8px; border-left: 4px solid #556ee6;">
                <h6 style="color: #556ee6; margin-bottom: 8px; font-weight: bold; font-size: 16px;">
                  <i class="bx bx-info-circle" style="margin-right: 5px;"></i>
                  Basic Information
                </h6>
                <div style="margin-bottom: 6px;">
                  <strong style="color: #495057; font-size: 14px;">Customer:</strong>
                  <span style="color: #6c757d; margin-left: 5px; font-size: 14px;">${customer?.customer_NameEn || 'N/A'}</span>
                </div>
                <div style="margin-bottom: 6px;">
                  <strong style="color: #495057; font-size: 14px;">Branch:</strong>
                  <span style="color: #6c757d; margin-left: 5px; font-size: 14px;">${branch?.branch_NameEn || 'N/A'}</span>
                </div>
                <div style="margin-bottom: 6px;">
                  <strong style="color: #495057; font-size: 14px;">Entity Type:</strong>
                  <span style="color: #6c757d; margin-left: 5px; font-size: 14px;">${membership.Entity_Type === 'pac' ? 'Package' : 'Package Series'}</span>
                </div>
                <div style="margin-bottom: 6px;">
                  <strong style="color: #495057; font-size: 14px;">Purchase Date:</strong>
                  <span style="color: #6c757d; margin-left: 5px; font-size: 14px;">${membership.PurchaseDate ? new Date(membership.PurchaseDate).toLocaleDateString('en-GB') : 'N/A'}</span>
                </div>
                <div style="margin-bottom: 0;">
                  <strong style="color: #495057; font-size: 14px;">Salesmen:</strong>
                  <span style="color: #6c757d; margin-left: 5px; font-size: 14px;">${salesmanName}</span>
                </div>
              </div>
              
              <div style="background: #f8f9fa; padding: 12px; border-radius: 8px; border-left: 4px solid #28a745;">
                <h6 style="color: #28a745; margin-bottom: 8px; font-weight: bold; font-size: 16px;">
                  <i class="bx bx-package" style="margin-right: 5px;"></i>
                  Membership Details
                </h6>
                <div style="margin-bottom: 6px;">
                  <strong style="color: #495057; font-size: 14px;">Entity Name:</strong>
                  <span style="color: #6c757d; margin-left: 5px; font-size: 14px;">${entityName || 'N/A'}</span>
                </div>
                <div style="margin-bottom: 6px;">
                  <strong style="color: #495057; font-size: 14px;">Amount:</strong>
                  <span style="color: #6c757d; margin-left: 5px; font-size: 14px;">${totalAmount.toFixed(2)}</span>
                </div>
                <div style="margin-bottom: 0;">
                  <strong style="color: #495057; font-size: 14px;">Remaining Balance:</strong>
                  <span style="color: #6c757d; margin-left: 5px; font-size: 14px;">${totalBalance.toFixed(2)}</span>
                </div>
              </div>
            </div>
            
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px;">
              <div style="background: #f8f9fa; padding: 12px; border-radius: 8px; border-left: 4px solid #ffc107;">
                <h6 style="color: #ffc107; margin-bottom: 8px; font-weight: bold; font-size: 16px;">
                  <i class="bx bx-spa" style="margin-right: 5px;"></i>
                  Package, Service & Product Details
                </h6>
                <div style="min-height: 100px;">
                  ${packageServiceDetailsHtml}
                </div>
              </div>
              
              <div style="background: #f8f9fa; padding: 12px; border-radius: 8px; border-left: 4px solid #17a2b8;">
                <h6 style="color: #17a2b8; margin-bottom: 8px; font-weight: bold; font-size: 16px;">
                  <i class="bx bx-credit-card" style="margin-right: 5px;"></i>
                  ${membership.Entity_Type === 'PAC' || membership.Entity_Type === 'pac' ? 'Package Payment' : 'Installments'}
                </h6>
                <div style="max-height: 180px; overflow-y: auto;">
                  ${installmentsHtml}
                </div>
              </div>
            </div>
          </div>
           <div style="text-align: center; margin-top: 16px;">
             <button
               id="membershipDetailsCloseBtn_${membership.MembershipId}"
               type="button"
               style="
                 background: #556ee6;
                 color: white;
                 border: none;
                 padding: 10px 22px;
                 border-radius: 6px;
                 cursor: pointer;
                 font-weight: 500;
                 font-size: 14px;
                 pointer-events: auto !important;
                 z-index: 20001;
                 position: relative;
               "
               onmouseover="this.style.background='#4858d4'"
               onmouseout="this.style.background='#556ee6'"
               onclick="if(window.__handleCloseClick_${membership.MembershipId}) { window.__handleCloseClick_${membership.MembershipId}(); } else { if(window.Swal) window.Swal.close(); }"
             >
               Close
             </button>
           </div>
        `,
        showConfirmButton: false,
        width: '900px',
        allowOutsideClick: false,
        allowEscapeKey: true,
        showClass: {
          popup: 'animate__animated animate__fadeInDown'
        },
        hideClass: {
          popup: 'animate__animated animate__fadeOutUp'
        },
        customClass: {
          container: 'swal2-top-layer',
          popup: 'swal2-custom-popup'
        },
        didOpen: () => {
          // Ensure SweetAlert is top-most so clicks don't "pass through"
          const container = Swal.getContainer?.();
          if (container) {
            container.style.zIndex = '20000';
          }

          // Create a robust close function that forces immediate close
          const forceCloseModal = () => {
            // Immediately try to close using Swal API
            try {
              Swal.close();
            } catch (e) {
              // Silently handle errors
            }

            // Safely remove SweetAlert elements
            const removeSwalElements = () => {
              try {
                // Get Swal elements using API
                const popup = Swal.getPopup?.();
                const container = Swal.getContainer?.();

                // Remove popup if it exists
                if (popup && popup.parentNode) {
                  popup.style.display = 'none';
                  popup.remove();
                }

                // Remove container if it exists
                if (container && container.parentNode) {
                  container.style.display = 'none';
                  container.remove();
                }

                // Remove backdrop/overlay - be more specific
                const backdrop = document.querySelector('.swal2-backdrop-show');
                if (backdrop && backdrop.parentNode) {
                  backdrop.style.display = 'none';
                  backdrop.remove();
                }

                // Also try the standard backdrop class
                const standardBackdrop = document.querySelector('.swal2-backdrop');
                if (standardBackdrop && standardBackdrop.parentNode && standardBackdrop !== backdrop) {
                  standardBackdrop.style.display = 'none';
                  standardBackdrop.remove();
                }

                // Safely remove body classes - check if body exists
                if (document.body) {
                  document.body.classList.remove('swal2-shown', 'swal2-height-auto', 'swal2-no-backdrop');
                  if (document.body.style) {
                    document.body.style.overflow = '';
                    document.body.style.paddingRight = '';
                  }
                }
              } catch (error) {
                // Silently handle errors
              }
            };

            // Execute immediately
            removeSwalElements();

            // Also execute after a short delay to catch any that appear
            setTimeout(removeSwalElements, 50);
            setTimeout(removeSwalElements, 150);
          };

          // Create global function for onclick fallback
          window[`__handleCloseClick_${membership.MembershipId}`] = forceCloseModal;

          // Use setTimeout to ensure DOM is fully ready
          setTimeout(() => {
            // Attach click handler to the view details button
            const viewDetailsBtn = document.getElementById(`viewDetailsBtn_${membership.MembershipId}`);
            if (viewDetailsBtn) {
              viewDetailsBtn.addEventListener('click', showDetailedItemsPopup);
            }

            // Attach click handler to the close button
            const closeBtn = document.getElementById(`membershipDetailsCloseBtn_${membership.MembershipId}`);

            if (closeBtn) {
              closeBtn.disabled = false;
              closeBtn.style.pointerEvents = 'auto';
              closeBtn.style.opacity = '1';
              closeBtn.style.cursor = 'pointer';
              closeBtn.style.zIndex = '20001';
              closeBtn.style.position = 'relative';

              // Create handler using the force close function
              const closeHandler = (e) => {
                e.preventDefault();
                e.stopPropagation();
                forceCloseModal();
              };

              // Remove any existing listener first
              if (closeBtn._closeHandler) {
                closeBtn.removeEventListener('click', closeBtn._closeHandler);
              }

              // Store handler reference for cleanup
              closeBtn._closeHandler = closeHandler;

              // Add event listener with capture to ensure it fires
              closeBtn.addEventListener('click', closeHandler, { capture: true });

              // Also try mousedown as fallback
              closeBtn.addEventListener('mousedown', (e) => {
                e.preventDefault();
                e.stopPropagation();
                forceCloseModal();
              });
            }
          }, 100);
        },
        didClose: () => {
          // Clean up global function
          if (window[`__handleCloseClick_${membership.MembershipId}`]) {
            delete window[`__handleCloseClick_${membership.MembershipId}`];
          }

          // Clean up event listeners when modal closes
          const viewDetailsBtn = document.getElementById(`viewDetailsBtn_${membership.MembershipId}`);
          if (viewDetailsBtn) {
            viewDetailsBtn.removeEventListener('click', showDetailedItemsPopup);
          }

          const closeBtn = document.getElementById(`membershipDetailsCloseBtn_${membership.MembershipId}`);
          if (closeBtn && closeBtn._closeHandler) {
            closeBtn.removeEventListener('click', closeBtn._closeHandler, { capture: true });
            delete closeBtn._closeHandler;
          }
        }
      });
    } catch (error) {
      console.error("Error viewing membership:", error);
      Swal.fire("Error", "Failed to view membership details", "error");
    }
  };

  const handleClear = () => {
    const defaultForm = getDefaultForm();
    setForm(defaultForm);
    setSelectedCustomerOption(null);
    setIsEditing(false);
    // Force form re-render by incrementing key
    setFormKey(prev => prev + 1);
  };

  const handleApplyFilter = () => {
    setHasAppliedFilter(true);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setCustomerSearchOptions([]);
    setSelectedCustomerFilterOption(null);
    setHasAppliedFilter(true);
  };

  const handleCustomerAdded = async (newCustomer) => {
    console.log("🔍 MembershipPage - handleCustomerAdded received:", newCustomer);

    // Get the customer ID from the response
    const customerId = newCustomer.customer_Id || newCustomer.id;
    console.log("🔍 Looking for customer ID:", customerId);

    try {
      // Try to fetch the specific customer by ID
      const customerData = await getCustomers({ customer_Id: customerId });
      console.log("🔍 Fetched customer data:", customerData);

      if (customerData && customerData.length > 0) {
        const addedCustomer = customerData[0];
        console.log("🔍 Found customer:", addedCustomer);

        // Set the newly added customer as selected
        setForm({ ...form, CustomerId: customerId });
        // Set the selected customer option for display
        setSelectedCustomerOption({
          value: customerId,
          label: `${addedCustomer.customer_NameEn || 'N/A'} — ${addedCustomer.customer_ContactNo || 'N/A'}`
        });
        console.log("🔍 Set customer option:", `${addedCustomer.customer_NameEn || 'N/A'} — ${addedCustomer.customer_ContactNo || 'N/A'}`);

        // Also reload the full customer list to keep it updated
        loadCustomers();
      } else {
        // Fallback: just set the ID and let the user search for the customer
        setForm({ ...form, CustomerId: customerId });
        setSelectedCustomerOption({
          value: customerId,
          label: `Customer ID: ${customerId}`
        });
        console.log("🔍 Customer not found, using fallback");

        // Still reload the full customer list
        loadCustomers();
      }
    } catch (error) {
      console.error("Error fetching customer:", error);
      // Fallback: just set the ID
      setForm({ ...form, CustomerId: customerId });
      setSelectedCustomerOption({
        value: customerId,
        label: `Customer ID: ${customerId}`
      });
      console.log("🔍 Error occurred, using fallback");
    }
  };


  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Calculate pagination
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = memberships.slice(startIndex, endIndex);

  // Options for dropdowns
  const customerOptions = customers.map(customer => ({
    value: customer.customer_Id,
    label: customer.customer_NameEn
  }));


  const packageOptions = spaPackages.map(pkg => ({
    value: pkg.packageId,
    label: pkg.packageName_En
  }));

  const packageSeriesOptions = spaPackageSeries.map(series => ({
    value: series.seriesId,
    label: series.seriesName_En
  }));

  const entityTypeOptions = [
    { value: 'pac', label: 'Package' },
    { value: 'sep', label: 'Package Series' }
  ];

  const entityTypeFilterOptions = [
    { value: 'pac', label: 'Package' },
    { value: 'sep', label: 'Package Series' }
  ];

  // Get names by ID
  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.customer_Id === customerId);
    return customer ? customer.customer_NameEn : "N/A";
  };

  const getBranchName = (branchId) => {
    const branch = branches.find(b => b.branch_Id === branchId);
    return branch ? branch.branch_NameEn : "N/A";
  };

  const getEntityName = (entityId, entityType) => {
    if (entityType === 'pac') {
      const pkg = spaPackages.find(p => p.packageId === entityId);
      return pkg ? pkg.packageName_En : "N/A";
    } else {
      const series = spaPackageSeries.find(s => s.seriesId === entityId);
      return series ? series.seriesName_En : "N/A";
    }
  };

  const getServiceName = (serviceId) => {
    const service = spaServices.find(s => s.spaService_Id === serviceId);
    return service ? service.spaServiceName_En : "N/A";
  };

  const getProductName = (productId) => {
    const product = products.find(p => p.product_Id === productId);
    return product ? product.product_NameEn : "N/A";
  };

  const getPackageName = (packageId) => {
    const pkg = spaPackages.find(p => p.packageId === packageId);
    return pkg ? pkg.packageName_En : "N/A";
  };

  const getEntityAmount = (entityId, entityType) => {
    if (entityType === 'pac') {
      const pkg = spaPackages.find(p => p.packageId === entityId);
      return pkg ? pkg.amount : 0;
    } else {
      const series = spaPackageSeries.find(s => s.seriesId === entityId);
      return series ? series.amount : 0;
    }
  };

  const getEmployeeName = (employeeId) => {
    if (!employeeId) return "N/A";
    const employee = employees.find(e => e.employee_Id === employeeId);
    return employee ? `${employee.employee_Code || ''} - ${employee.employee_NameEn || 'N/A'}`.trim() : "N/A";
  };

  if (!hasPermission) {
    return null;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Membership Management" breadcrumbItem="Spa" />

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody className="p-0">
                <Nav tabs className="nav-tabs-custom">
                  {hasEditOrHigher && (
                    <NavItem>
                      <NavLink
                        style={{
                          cursor: "pointer",
                          fontWeight: activeTab === "1" ? "bold" : "normal",
                          color: activeTab === "1" ? "#556ee6" : "#74788d",
                          borderBottom: activeTab === "1" ? "2px solid #556ee6" : "none",
                          padding: "15px 25px",
                          fontSize: "16px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px"
                        }}
                        className={activeTab === "1" ? "active" : ""}
                        onClick={() => setActiveTab("1")}
                      >
                        <i className="bx bx-plus-circle"></i>
                        <span>Add Membership</span>
                      </NavLink>
                    </NavItem>
                  )}
                  <NavItem>
                    <NavLink
                      style={{
                        cursor: "pointer",
                        fontWeight: activeTab === "2" ? "bold" : "normal",
                        color: activeTab === "2" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "2" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                      className={activeTab === "2" ? "active" : ""}
                      onClick={() => setActiveTab("2")}
                    >
                      <i className="bx bx-list-ul"></i>
                      <span>Membership List</span>
                    </NavLink>
                  </NavItem>
                </Nav>
                <TabContent activeTab={activeTab} className="p-4">
                  <TabPane tabId="1">
                    <div>
                      <Form key={formKey} onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="CustomerId" style={{ fontSize: '14px' }}>Customer <span className="text-danger">*</span></Label>
                              <div className="d-flex gap-2">
                                <div style={{ flex: 1 }}>
                                  <div className="d-flex gap-2">
                                    <div style={{ flex: 1 }}>
                                      <Select
                                        key={`customer-${formKey}`}
                                        value={selectedCustomerOption}
                                        onChange={(selected) => {
                                          setSelectedCustomerOption(selected);
                                          setForm({ ...form, CustomerId: selected?.value || null });
                                        }}
                                        options={customerSearchOptions}
                                        placeholder="Type at least 3 characters to search customers..."
                                        styles={selectStyles}
                                        isClearable
                                        isSearchable
                                        maxMenuHeight={200}
                                        isLoading={customerSearchLoading}
                                        noOptionsMessage={() => "Type at least 3 characters to search"}
                                        loadingMessage={() => "Searching customers..."}
                                        onInputChange={(inputValue) => {
                                          console.log('🔤 Input changed:', inputValue);

                                          // Clear previous timeout
                                          if (searchTimeout) {
                                            clearTimeout(searchTimeout);
                                          }

                                          // Set new timeout
                                          const timeout = setTimeout(() => {
                                            searchCustomersManually(inputValue);
                                          }, 300);
                                          setSearchTimeout(timeout);

                                          return inputValue;
                                        }}
                                        onMenuOpen={() => console.log('📂 Menu opened')}
                                        onMenuClose={() => console.log('📁 Menu closed')}
                                      />
                                    </div>
                                    <Button
                                      color="info"
                                      size="sm"
                                      onClick={() => setShowAdvancedSearchModal(true)}
                                      title="Advanced Customer Search"
                                      style={{ height: '38px' }}
                                    >
                                      <Search size={16} />
                                    </Button>
                                  </div>
                                </div>
                                <Button
                                  color="success"
                                  size="sm"
                                  onClick={() => setShowQuickAddCustomer(true)}
                                  disabled={isEditing}
                                  title="Quick Add Customer"
                                >
                                  <i className="fas fa-plus"></i>
                                </Button>
                              </div>
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="BranchId" style={{ fontSize: '14px' }}>Branch <span className="text-danger">*</span></Label>
                              <Select
                                key={`branch-${formKey}`}
                                value={form.BranchId ? branchOptions.find(option => option.value === form.BranchId) : null}
                                onChange={(selected) => setForm({ ...form, BranchId: selected?.value || null })}
                                options={branchOptions}
                                placeholder="Select Branch"
                                styles={selectStyles}
                                isClearable={!isBranchReadOnly}
                                isSearchable
                                maxMenuHeight={200}
                                isDisabled={!!localStorage.getItem("userBranchId")}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="Entity_Type" style={{ fontSize: '14px' }}>Entity Type <span className="text-danger">*</span></Label>
                              <Select
                                key={`entity-type-${formKey}`}
                                value={form.Entity_Type ? entityTypeOptions.find(option => option.value === form.Entity_Type) : null}
                                onChange={(selected) => {
                                  setForm({
                                    ...form,
                                    Entity_Type: selected?.value || null,
                                    Entity_Id: null // Reset entity selection when type changes
                                  });
                                }}
                                options={entityTypeOptions}
                                placeholder="Select Entity Type"
                                styles={selectStyles}
                                isClearable
                                maxMenuHeight={200}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="Entity_Id" style={{ fontSize: '14px' }}>
                                {form.Entity_Type === 'pac' ? 'Package' : form.Entity_Type === 'sep' ? 'Package Series' : 'Entity'}
                                <span className="text-danger">*</span>
                              </Label>
                              <Select
                                key={`entity-id-${formKey}`}
                                value={form.Entity_Id
                                  ? (form.Entity_Type === 'pac'
                                    ? packageOptions.find(option => option.value === form.Entity_Id)
                                    : packageSeriesOptions.find(option => option.value === form.Entity_Id))
                                  : null
                                }
                                onChange={(selected) => setForm({ ...form, Entity_Id: selected?.value || null })}
                                options={form.Entity_Type === 'pac' ? packageOptions : packageSeriesOptions}
                                placeholder={`Select ${form.Entity_Type === 'pac' ? 'Package' : form.Entity_Type === 'sep' ? 'Package Series' : 'Entity'}`}
                                styles={selectStyles}
                                isClearable
                                isSearchable
                                maxMenuHeight={200}
                                isDisabled={!form.Entity_Type}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="PurchaseDate">Purchase Date <span className="text-danger">*</span></Label>
                              <Input
                                id="PurchaseDate"
                                name="PurchaseDate"
                                type="date"
                                value={form.PurchaseDate}
                                onChange={(e) => setForm({ ...form, PurchaseDate: e.target.value })}
                                required
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="SalesmanId" style={{ fontSize: '14px' }}>Salesman <span className="text-danger">*</span></Label>
                              <div
                                title={(() => {
                                  // Get all selected salesmen names for tooltip
                                  const salesmanIds = form.SalesmanIds && Array.isArray(form.SalesmanIds) && form.SalesmanIds.length > 0
                                    ? form.SalesmanIds
                                    : (form.SalesmanId && form.SalesmanId > 0 ? [form.SalesmanId] : []);

                                  if (salesmanIds.length === 0) return "No salesmen selected";

                                  const salesmanNames = salesmanIds
                                    .map(id => {
                                      const employee = employees.find(emp => parseInt(emp.employee_Id) === parseInt(id));
                                      return employee ? employee.employee_NameEn : null;
                                    })
                                    .filter(name => name !== null);

                                  return salesmanNames.length > 0 ? salesmanNames.join(", ") : "No salesmen selected";
                                })()}
                                style={{ cursor: 'help' }}
                              >
                                <Select
                                  isMulti
                                  key={`salesman-${formKey}`}
                                  value={(() => {
                                    // Use SalesmanIds array if available, otherwise fallback to single SalesmanId
                                    const salesmanIds = form.SalesmanIds && Array.isArray(form.SalesmanIds) && form.SalesmanIds.length > 0
                                      ? form.SalesmanIds
                                      : (form.SalesmanId && form.SalesmanId > 0 ? [form.SalesmanId] : []);

                                    // Convert salesman IDs to select options
                                    return salesmanIds
                                      .map(id => {
                                        const employee = employees.find(emp => parseInt(emp.employee_Id) === parseInt(id));
                                        return employee ? { value: employee.employee_Id, label: employee.employee_NameEn } : null;
                                      })
                                      .filter(opt => opt !== null);
                                  })()}
                                  onChange={(selectedOptions) => {
                                    // Handle both single value (backward compatibility) and array of options
                                    const salesmanIds = selectedOptions
                                      ? (Array.isArray(selectedOptions) ? selectedOptions.map(opt => parseInt(opt.value || opt)) : [parseInt(selectedOptions.value || selectedOptions)])
                                      : [];
                                    const firstSalesmanId = salesmanIds.length > 0 ? salesmanIds[0] : null;

                                    setForm({
                                      ...form,
                                      SalesmanId: firstSalesmanId,  // Keep for backward compatibility
                                      SalesmanIds: salesmanIds  // Array of salesman IDs
                                    });
                                  }}
                                  options={employeeOptions}
                                  placeholder="Select Salesmen"
                                  styles={selectStyles}
                                  isClearable
                                  isSearchable
                                  maxMenuHeight={200}
                                  menuPortalTarget={document.body}
                                />
                              </div>
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row className="mt-4">
                          <Col>
                            <div className="d-flex gap-2">
                              <Button
                                type="submit"
                                color="primary"
                                disabled={saveLoading || !form.CustomerId || !form.BranchId || !form.Entity_Type || !form.Entity_Id || !form.PurchaseDate || ((!form.SalesmanIds || form.SalesmanIds.length === 0) && (!form.SalesmanId || form.SalesmanId <= 0))}
                                className="waves-effect waves-light"
                              >
                                {saveLoading ? (
                                  <>
                                    <ClipLoader size={16} color="#ffffff" className="me-2" />
                                    <span>Saving...</span>
                                  </>
                                ) : (
                                  isEditing ? "Update Membership" : "Save Membership"
                                )}
                              </Button>
                              <Button
                                type="button"
                                color="secondary"
                                onClick={handleClear}
                                className="waves-effect waves-light"
                              >
                                Clear
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    </div>
                  </TabPane>

                  <TabPane tabId="2">
                    <div>
                      <Card className="mb-4">
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center">
                            <h5 className="card-title mb-3">Filter Memberships</h5>
                            <Button
                              style={{
                                backgroundColor: '#556ee6',
                                borderColor: '#556ee6',
                                color: 'white',
                                borderRadius: '6px',
                                padding: '8px 16px',
                                fontSize: '14px',
                                fontWeight: '500',
                                border: 'none',
                                cursor: 'pointer'
                              }}
                              onClick={() => setShowFilters(!showFilters)}
                            >
                              {showFilters ? "Hide Filters" : "Show Filters"}
                            </Button>
                          </div>
                          {showFilters && (
                            <Row className="g-3">
                              <Col md={3}>
                                <Label for="filterCustomerId">Customer</Label>
                                <Select
                                  value={selectedCustomerFilterOption}
                                  onChange={(selected) => {
                                    setSelectedCustomerFilterOption(selected);
                                    setFilter({ ...filter, CustomerId: selected?.value || null });
                                  }}
                                  options={customerSearchOptions}
                                  placeholder="Type at least 3 characters to search customers..."
                                  styles={selectStyles}
                                  isClearable
                                  isSearchable
                                  isLoading={customerSearchLoading}
                                  noOptionsMessage={() => "Type at least 3 characters to search"}
                                  loadingMessage={() => "Searching customers..."}
                                  onInputChange={(inputValue) => {
                                    console.log('🔤 Input changed:', inputValue);

                                    // Clear previous timeout
                                    if (searchTimeout) {
                                      clearTimeout(searchTimeout);
                                    }

                                    // Set new timeout
                                    const timeout = setTimeout(() => {
                                      searchCustomersManually(inputValue);
                                    }, 300);
                                    setSearchTimeout(timeout);

                                    return inputValue;
                                  }}
                                  onMenuOpen={() => console.log('📂 Menu opened')}
                                  onMenuClose={() => console.log('📁 Menu closed')}
                                />
                              </Col>
                              <Col md={3}>
                                <Label for="filterBranchId">Branch</Label>
                                <Select
                                  value={branchOptions.find(option => option.value === filter.BranchId) || null}
                                  onChange={(selected) => setFilter({ ...filter, BranchId: selected?.value || null })}
                                  options={branchOptions}
                                  placeholder="Select Branch"
                                  styles={selectStyles}
                                  isClearable
                                  isSearchable
                                />
                              </Col>
                              <Col md={3}>
                                <Label for="filterEntityType">Entity Type</Label>
                                <Select
                                  value={entityTypeFilterOptions.find(option => option.value === filter.Entity_Type)}
                                  onChange={(selected) => setFilter({ ...filter, Entity_Type: selected?.value || null })}
                                  options={entityTypeFilterOptions}
                                  placeholder="Select Entity Type"
                                  styles={selectStyles}
                                  isClearable
                                />
                              </Col>
                              <Col md={3}>
                                <Label for="filterSalesmanId">Salesman</Label>
                                <Select
                                  value={filter.SalesmanId ? employeeOptions.find(option => option.value === filter.SalesmanId) : null}
                                  onChange={(selected) => setFilter({ ...filter, SalesmanId: selected?.value || null })}
                                  options={employeeOptions}
                                  placeholder="Select Salesman"
                                  styles={selectStyles}
                                  isClearable
                                  isSearchable
                                />
                              </Col>
                              <Col md={3}>
                                <Label for="filterFromDate">From Date</Label>
                                <Input
                                  id="filterFromDate"
                                  name="filterFromDate"
                                  type="date"
                                  value={filter.From_Date ? filter.From_Date.split('T')[0] : ''}
                                  onChange={(e) => setFilter({ ...filter, From_Date: e.target.value ? new Date(e.target.value).toISOString() : null })}
                                />
                              </Col>
                              <Col md={3}>
                                <Label for="filterToDate">To Date</Label>
                                <Input
                                  id="filterToDate"
                                  name="filterToDate"
                                  type="date"
                                  value={filter.To_Date ? filter.To_Date.split('T')[0] : ''}
                                  onChange={(e) => setFilter({ ...filter, To_Date: e.target.value ? new Date(e.target.value).toISOString() : null })}
                                />
                              </Col>
                              <Col md={12} className="d-flex align-items-end">
                                <div className="d-flex gap-2">
                                  <Button
                                    color="primary"
                                    onClick={handleApplyFilter}
                                    className="waves-effect waves-light"
                                  >
                                    Apply Filter
                                  </Button>
                                  <Button
                                    color="secondary"
                                    onClick={handleClearFilter}
                                    className="waves-effect waves-light"
                                  >
                                    Clear Filter
                                  </Button>
                                </div>
                              </Col>
                            </Row>
                          )}
                        </CardBody>
                      </Card>

                      <Card>
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="card-title mb-0">
                              Membership List
                            </h5>
                            {hasEditOrHigher && (
                              <Button
                                color="primary"
                                onClick={() => setActiveTab("1")}
                                className="waves-effect waves-light"
                              >
                                <i className="bx bx-plus me-1"></i>
                                Add New Membership
                              </Button>
                            )}
                          </div>

                          {loading ? (
                            <div className="text-center p-4">
                              <ClipLoader size={50} color="#556ee6" />
                              <p className="mt-2">Loading memberships...</p>
                            </div>
                          ) : (
                            <>
                              <div className="table-responsive">
                                <Table className="table table-bordered table-striped table-hover">
                                  <thead className="table-light">
                                    <tr>
                                      <th width="5%">#</th>
                                      <th width="12%">Branch Name</th>
                                      <th width="18%">Customer Name</th>
                                      <th width="12%">Entity Type</th>
                                      <th width="20%">Entity Name</th>
                                      <th width="15%">Purchase Date</th>
                                      <th width="10%">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {paginatedData.length > 0 ? (
                                      paginatedData.map((item, index) => (
                                        <tr key={item.MembershipId}>
                                          <td>{startIndex + index + 1}</td>
                                          <td>{getBranchName(item.BranchId)}</td>
                                          <td>{getCustomerName(item.CustomerId)}</td>
                                          <td>
                                            <span className={`badge ${item.Entity_Type === 'pac' ? 'bg-primary' : 'bg-info'}`}>
                                              {item.Entity_Type === 'pac' ? 'Package' : 'Package Series'}
                                            </span>
                                          </td>
                                          <td>{getEntityName(item.Entity_Id, item.Entity_Type)}</td>
                                          <td>{item.PurchaseDate ? new Date(item.PurchaseDate).toLocaleDateString('en-GB') : 'N/A'}</td>
                                          <td>
                                            <div className="d-flex gap-2">
                                              <Button
                                                color="info"
                                                size="sm"
                                                onClick={() => handleView(item)}
                                                className="waves-effect waves-light"
                                              >
                                                <Eye size={12} />
                                              </Button>
                                              {hasEditOrHigher && (
                                                <Button
                                                  color="primary"
                                                  size="sm"
                                                  onClick={() => handleEdit(item)}
                                                  disabled={editLoadingRow === item.MembershipId}
                                                  className="waves-effect waves-light"
                                                >
                                                  {editLoadingRow === item.MembershipId ? (
                                                    <ClipLoader size={12} color="#ffffff" />
                                                  ) : (
                                                    <SquarePen size={12} />
                                                  )}
                                                </Button>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td colSpan="8" className="text-center">
                                          {hasAppliedFilter ? "No memberships found matching the filter criteria." : "No memberships available. Please add some memberships first."}
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </Table>
                              </div>

                              {/* Pagination */}
                              {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                  <div>
                                    <p className="text-muted mb-0">
                                      Showing {startIndex + 1} to {Math.min(endIndex, totalCount)} of {totalCount} entries
                                    </p>
                                  </div>
                                  <div className="d-flex gap-2">
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => handlePageChange(page - 1)}
                                      disabled={page === 1}
                                    >
                                      Previous
                                    </Button>
                                    <span className="align-self-center mx-2">
                                      Page {page} of {totalPages}
                                    </span>
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => handlePageChange(page + 1)}
                                      disabled={page === totalPages}
                                    >
                                      Next
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </CardBody>
                      </Card>
                    </div>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Quick Add Customer Modal */}
      <QuickAddCustomer
        isOpen={showQuickAddCustomer}
        toggle={() => setShowQuickAddCustomer(false)}
        onCustomerAdded={handleCustomerAdded}
      />

      {/* Customer Profile Modal */}
      <CustomerProfileModal
        isOpen={showCustomerProfile}
        toggle={() => setShowCustomerProfile(false)}
        customerId={selectedCustomerOption?.value || null}
        customer={selectedCustomerOption ? { customer_Id: selectedCustomerOption.value } : null}
      />

      {/* Advanced Customer Search Modal */}
      <CustomerAdvancedSearchModal
        isOpen={showAdvancedSearchModal}
        toggle={() => setShowAdvancedSearchModal(false)}
        userBranchId={getUserSession().branchId}
        onCustomerSelected={(customer) => {
          // Set the selected customer in the form and dropdown
          setForm(prev => ({
            ...prev,
            CustomerId: customer.customer_Id
          }));
          setSelectedCustomerOption({
            value: customer.customer_Id,
            label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
            customer: customer
          });
        }}
      />
    </div>
  );
}
